import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  Typography,
} from "@mui/material";
import { DriveFileMoveOutlined } from "@mui/icons-material";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";

import FormFooter, {
  FOOTER_BUTTON_SX,
} from "components/userManagement/form/FormFooter";
import FormHeader from "components/userManagement/form/FormHeader";
import { pushNotification } from "features/notificationSlice";
import {
  useCreateSecureCollectionMutation,
  useGetSecureBulkMoveCheckQuery,
  useGetSecureProjectCollectionsQuery,
  useGetSecureProjectsQuery,
  useMoveSecureFilesMutation,
} from "features/secureUploads/secureUploads";
import useResponsive from "hooks/useResponsive";
import { useViewAs } from "hooks/useViewAs";
import { formatApiError } from "utils/formatApiError";

import { MoveImpactAlerts, MoveTargetFields, NEW, ROOT } from "./moveParts";
import { moveConfirmLabel, moveRefusal } from "./moveModel";
import type { SelectedFile } from "./selectionModel";

/** Add a selection of Individual Files to a project, after seeing who would lose
 * them and who would newly see them - all of them or none. */
export default function AddFilesToProjectDialog({
  files,
  clientId,
  onClose,
  onMoved,
}: {
  /** null: closed. */
  files: SelectedFile[] | null;
  clientId: number;
  onClose: () => void;
  onMoved: (ids: number[]) => void;
}) {
  const dispatch = useDispatch();
  const { readOnly } = useViewAs();
  const { isPhone } = useResponsive();
  const [projectChoice, setProjectChoice] = useState("");
  const [collectionChoice, setCollectionChoice] = useState(ROOT);
  const [newName, setNewName] = useState("");
  const [failure, setFailure] = useState<string | null>(null);
  const open = files !== null;
  const ids = files?.map((file) => file.id) ?? [];

  useEffect(() => {
    setProjectChoice("");
    setCollectionChoice(ROOT);
    setNewName("");
    setFailure(null);
  }, [files]);

  const projectId = Number(projectChoice) || null;
  const { data: openProjects } = useGetSecureProjectsQuery(
    { client_id: clientId, status: "open", page_size: 200 },
    { skip: !open },
  );
  const { data: collections = [] } = useGetSecureProjectCollectionsQuery(
    projectId ?? 0,
    { skip: !open || !projectId },
  );
  const collectionId =
    collectionChoice === ROOT || collectionChoice === NEW
      ? null
      : Number(collectionChoice);
  // currentData: a check that failed for this target must not leave the last
  // target's people on screen, or the move would add people nobody was shown.
  const {
    currentData: check,
    isFetching: checking,
    error: checkError,
    refetch: checkAgain,
  } = useGetSecureBulkMoveCheckQuery(
    { ids, project_id: projectId ?? 0, collection_id: collectionId },
    { skip: !open || !projectId || ids.length === 0 },
  );
  const [move, { isLoading: moving }] = useMoveSecureFilesMutation();
  const [createCollection, { isLoading: creating }] =
    useCreateSecureCollectionMutation();
  const busy = moving || creating;

  if (!files) return null;

  const projects = openProjects?.results ?? [];
  const losing = check?.losing ?? [];
  const gaining = check?.gaining ?? [];
  const ready =
    Boolean(projectId) &&
    !(collectionChoice === NEW && !newName.trim()) &&
    !checking &&
    !checkError &&
    check !== undefined;
  const projectName =
    projects.find((project) => project.id === projectId)?.name ?? "the project";
  const checkFailure = checkError
    ? (checkError as { status?: number }).status === 404
      ? "Some of these files are no longer here. Close this and select them again."
      : formatApiError(
          (checkError as { data?: unknown }).data,
          "The move could not be checked.",
        )
    : null;

  const submit = async () => {
    if (!projectId) return;
    setFailure(null);
    try {
      let target = collectionId;
      if (collectionChoice === NEW) {
        const made = await createCollection({
          projectId,
          name: newName.trim(),
        }).unwrap();
        target = made.id;
        // A retry after a refused move goes into it instead of making it again.
        setCollectionChoice(String(made.id));
        setNewName("");
      }
      const done = await move({
        ids,
        project_id: projectId,
        collection_id: target,
        add_members: losing.length > 0,
      }).unwrap();
      dispatch(
        pushNotification({
          message:
            done.moved === 1
              ? `File added to ${done.to}.`
              : `${done.moved} files added to ${done.to}.`,
          severity: "success",
        }),
      );
      onMoved(ids);
      onClose();
    } catch (error) {
      setFailure(
        moveRefusal(
          (error as { data?: unknown })?.data,
          "The files could not be moved.",
        ),
      );
      // Who would lose them may have changed since the check: ask again.
      void checkAgain();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isPhone}
      PaperProps={{ sx: { borderRadius: isPhone ? 0 : 2, overflow: "hidden" } }}
    >
      <DialogContent sx={{ p: 0 }}>
        <FormHeader
          avatar={<DriveFileMoveOutlined fontSize="small" />}
          title="Add to Project"
          onClose={onClose}
          closeDisabled={busy}
        />
        <Box sx={{ p: 3, display: "grid", gap: 2 }}>
          <Typography variant="body2" color="text.secondary">
            <b>
              {files.length === 1
                ? files[0].file_name
                : `${files.length} files`}
            </b>{" "}
            - from Individual Files
          </Typography>
          <MoveTargetFields
            projects={projects}
            projectChoice={projectChoice}
            onProject={(value) => {
              setProjectChoice(value);
              setCollectionChoice(ROOT);
            }}
            projectId={projectId}
            collections={collections}
            collectionChoice={collectionChoice}
            onCollection={setCollectionChoice}
            newName={newName}
            onNewName={setNewName}
            disabled={busy}
          />
          <MoveImpactAlerts
            losing={losing}
            gaining={gaining}
            projectName={projectName}
            files={files.length}
          />
          {failure || checkFailure ? (
            <Alert severity="error">{failure ?? checkFailure}</Alert>
          ) : null}
        </Box>
      </DialogContent>
      <FormFooter
        mode="edit"
        isFirstStep
        onBack={() => undefined}
        onCancel={onClose}
        cancelDisabled={busy}
      >
        <Button
          variant="contained"
          size="small"
          onClick={submit}
          disabled={!ready || busy || readOnly}
          sx={FOOTER_BUTTON_SX}
        >
          {busy ? "Moving..." : moveConfirmLabel(losing, files.length)}
        </Button>
      </FormFooter>
    </Dialog>
  );
}
